import {
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  HorizontalOriginOption,
  VerticalOriginOption,
} from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { SvgDatasource14pxSmall } from '../../../gen-src/icons/Datasource14pxSmall';
import { SvgDatasourceCertified14pxSmall } from '../../../gen-src/icons/DatasourceCertified14pxSmall';
import { SvgTable14pxSmall } from '../../../gen-src/icons/Table14pxSmall';
import { SvgTableCertified14pxSmall } from '../../../gen-src/icons/TableCertified14pxSmall';
import { sharedDialogTelemetry } from '../telemetry/SharedDialogTelemetry';
import {
  getTelemetryEnvironmentEnumValueFromString,
  TelemetryContext,
  TelemetryEnvironment
} from '../telemetry/TelemetryContext';
import { telemetrySender } from '../telemetry/TelemetrySender';
import {
  ColumnDescriptionCell,
  ColumnDescriptionFieldName,
  ColumnDescriptionInheritedColumnRow,
  ColumnDescriptionPane,
  DescriptionLabel,
  DescriptionText,
  dialogWidth,
  InheritedDetailsLabel,
  InheritedDetailsValue,
  InheritedRow,
  AssetParentIconStyle,
  DescriptionDialogBodyStyle,
} from './ColumnDescriptionDialogStyles';

export interface AssetParentProps {
  contentType: 'datasource' | 'table';
  isCertified: boolean;
  name: string;
}

export interface ColumnDescriptionDialogProps {
  readonly anchorEl: HTMLElement;
  readonly assetParent: AssetParentProps;
  readonly description: string | null;
  readonly fieldName: string;
  readonly inheritedColumnName: string | null;
  readonly siteLuid: string;
  readonly tableauVersion: string;
  readonly telemetryEnvironment: string;
  // telemetry-contract-api requires this particular signature for its callback
  // eslint-disable-next-line @typescript-eslint/ban-types
  readonly telemetryContractApi?: (eventType: string, payload: {}[]) => void;
  readonly onClose: () => void;
}

export function InheritedRowDetails(props: {
  label: string | null;
  value: string | null;
  icon?: JSX.Element;
}): JSX.Element | null {
  if (props.value == null) {
    return null;
  }
  return (
    <InheritedRow>
      <InheritedDetailsLabel>
        {props.label} {props.icon || null}
      </InheritedDetailsLabel>

      <InheritedDetailsValue>{props.value}</InheritedDetailsValue>
    </InheritedRow>
  );
}

export class ColumnDescriptionDialog extends React.Component<
  ColumnDescriptionDialogProps
> {
  private displayStartTime: number = Date.now();
  private sentDialogClosedTelemetry = false;

  private sendDialogClosedTelemetry = (): void => {
    if (this.sentDialogClosedTelemetry) { return; }

    const displayEndTime: number = Date.now();
    const displayDuration = displayEndTime - this.displayStartTime;

    const typeOfDataDisplayed = 'column-description';

    const telemetryEnvironment: TelemetryEnvironment = getTelemetryEnvironmentEnumValueFromString(
      this.props.telemetryEnvironment);
    const dialogReleaseVersion: string = this.props.tableauVersion;
    const siteLuid: string = this.props.siteLuid;
    const telemetryContext: TelemetryContext = {
      telemetryEnvironment: telemetryEnvironment,
      dialogReleaseVersion: dialogReleaseVersion,
      siteLuid: siteLuid,
    };

    if (this.props.telemetryContractApi) {
      telemetrySender.setTelemetryContractApiCallback(this.props.telemetryContractApi);
      sharedDialogTelemetry.sendDataDisplayedInSharedDialog(typeOfDataDisplayed, displayDuration, telemetryContext);
    }

    this.sentDialogClosedTelemetry = true;
  }

  private onClose = (): void => {
    this.sendDialogClosedTelemetry();
    this.props.onClose();
  }

  public componentWillUnmount(): void {
    this.sendDialogClosedTelemetry();
  }

  public render(): JSX.Element {
    const shouldShowInheritedDetails =
      !!this.props.inheritedColumnName || !!this.props.assetParent;

    return (
      <DialogWidget
        anchorEl={this.props.anchorEl}
        anchorOrigin={{
          vertical: VerticalOriginOption.top,
          horizontal: HorizontalOriginOption.right,
        }}
        dialogBodyStyle={DescriptionDialogBodyStyle}
        dialogWidth={dialogWidth}
        disableAutoFocus={true}
        isModal={false}
        onCloseRequest={this.onClose}
        positioningBehavior={DialogPositioningBehavior.Anchored}
        showCloseButton={false}
        showFooter={false}
        testId='column-description'
        title={Messages.ColumnDescription()}
        type={DialogType.Uber}
      >
        <ColumnDescriptionPane>
          <ColumnDescriptionFieldName>
            {this.props.fieldName}
          </ColumnDescriptionFieldName>
          <ColumnDescriptionCell>
            <DescriptionLabel>{Messages.Description()}</DescriptionLabel>
            <DescriptionText>{this.props.description}</DescriptionText>
          </ColumnDescriptionCell>
          {shouldShowInheritedDetails && this.renderInheritanceDetails()}
        </ColumnDescriptionPane>
      </DialogWidget>
    );
  }

  private renderInheritanceDetails(): React.ReactNode {
    return (
      <ColumnDescriptionCell>
        {!!this.props.inheritedColumnName && (
          <ColumnDescriptionInheritedColumnRow>
            <InheritedRowDetails
              label={Messages.DescriptionInheritanceColumnLabel()}
              value={this.props.inheritedColumnName}
            />
          </ColumnDescriptionInheritedColumnRow>
        )}
        {!!this.props.assetParent && this.renderAssetParent()}
      </ColumnDescriptionCell>
    );
  }

  private renderAssetParent(): React.ReactNode {
    let label: string | null = null;
    let icon: JSX.Element;

    switch (this.props.assetParent.contentType) {
      case 'datasource':
        label = Messages.DescriptionInheritanceDatasourceLabel();
        icon = this.props.assetParent.isCertified ? (
          <SvgDatasourceCertified14pxSmall
            style={{
              padding: '0 3px',
              verticalAlign: 'top',
            }}
          />
        ) : (
          <SvgDatasource14pxSmall
            style={{
              padding: '0 3px',
              verticalAlign: 'top',
            }}
          />
        );
        break;

      case 'table':
        label = Messages.DescriptionInheritanceTableLabel();
        icon = this.props.assetParent.isCertified ? (
          <SvgTableCertified14pxSmall style={AssetParentIconStyle} />
        ) : (
          <SvgTable14pxSmall style={AssetParentIconStyle} />
        );
        break;

      default:
        return;
    }

    return (
      <ColumnDescriptionInheritedColumnRow>
        <InheritedRowDetails
          label={label}
          value={this.props.assetParent.name}
          icon={icon}
        />
      </ColumnDescriptionInheritedColumnRow>
    );
  }
}
